import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Sparkles } from 'lucide-react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  aircraftTypeFormSchema,
  type AircraftTypeFormInput,
  type AircraftTypeFormValues,
} from './aircraft-type-form-schema'
import type { AircraftTypeRow, AircraftCategory, GearType } from '@/types/aircraft-types'
import { AIRCRAFT_CATEGORY_LABELS, AIRCRAFT_CATEGORIES, GEAR_TYPE_LABELS } from '@/types/aircraft-types'
import { AircraftSearchAutocomplete } from './AircraftSearchAutocomplete'
import { ModelUpload } from './ModelUpload'
import type { ExternalAircraftData } from '@/hooks/useAircraftSearch'

interface AircraftTypeFormProps {
  aircraftType?: AircraftTypeRow | null
  onSubmit: (data: AircraftTypeFormValues) => void
  onCancel: () => void
  isLoading?: boolean
  /** Show API search section (for new aircraft types only) */
  showApiSearch?: boolean
}

/**
 * Generate a type_code from manufacturer and model
 * e.g., "Cessna" + "Citation CJ3" -> "cessna-citation-cj3"
 */
function generateTypeCode(manufacturer: string, model: string, variant?: string): string {
  const parts = [manufacturer, model]
  if (variant) {
    parts.push(variant)
  }
  return parts
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Remove multiple dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
}

export function AircraftTypeForm({
  aircraftType,
  onSubmit,
  onCancel,
  isLoading,
  showApiSearch = true,
}: AircraftTypeFormProps) {
  const form = useForm<AircraftTypeFormInput, unknown, AircraftTypeFormValues>({
    resolver: zodResolver(aircraftTypeFormSchema),
    defaultValues: {
      type_code: aircraftType?.type_code || '',
      manufacturer: aircraftType?.manufacturer || '',
      model: aircraftType?.model || '',
      variant: aircraftType?.variant || '',
      category: aircraftType?.category || null,
      length: aircraftType?.length || 0,
      wingspan: aircraftType?.wingspan || 0,
      height: aircraftType?.height || 0,
      tail_height: aircraftType?.tail_height ?? undefined,
      empty_weight: aircraftType?.empty_weight ?? undefined,
      max_takeoff_weight: aircraftType?.max_takeoff_weight ?? undefined,
      max_ramp_weight: aircraftType?.max_ramp_weight ?? undefined,
      model_file_path: aircraftType?.model_file_path || '',
      model_scale: aircraftType?.model_scale ?? 1.0,
      heading_axis: (aircraftType?.heading_axis as 'x' | 'y' | 'z' | '-x' | '-y' | '-z') || 'y',
      vertical_axis: (aircraftType?.vertical_axis as 'x' | 'y' | 'z' | '-x' | '-y' | '-z') || 'z',
      typical_value_new: aircraftType?.typical_value_new ?? undefined,
      typical_value_used: aircraftType?.typical_value_used ?? undefined,
      hangar_rate_typical: aircraftType?.hangar_rate_typical ?? undefined,
      notes: aircraftType?.notes || '',
      is_active: aircraftType?.is_active ?? true,
      gear_type: aircraftType?.gear_type || null,
      main_gear_wheels: aircraftType?.main_gear_wheels ?? undefined,
      nose_gear_steering_angle: aircraftType?.nose_gear_steering_angle ?? undefined,
      nose_gear_from_nose: aircraftType?.nose_gear_from_nose ?? undefined,
      main_gear_from_nose: aircraftType?.main_gear_from_nose ?? undefined,
      wheel_track: aircraftType?.wheel_track ?? undefined,
      wheelbase: aircraftType?.wheelbase ?? undefined,
      turning_radius: aircraftType?.turning_radius ?? undefined,
      icao_designator: aircraftType?.icao_designator || '',
      external_api_id: aircraftType?.external_api_id ?? undefined,
    },
  })

  const isEdit = !!aircraftType

  /**
   * Handle aircraft data from API autocomplete
   * Maps external API fields to form fields
   */
  const handleApiDataSelect = useCallback(
    (data: ExternalAircraftData) => {
      // Generate type_code from manufacturer and model
      const typeCode = generateTypeCode(
        data.manufacturer,
        data.model,
        data.variant
      )

      // Map API data to form values
      // Note: API may use meters/kg, our form uses feet/lbs
      // For now, assume API returns data in our expected units
      // TODO: Add unit conversion if API uses different units
      form.reset({
        // Basic info
        type_code: typeCode,
        manufacturer: data.manufacturer,
        model: data.model,
        variant: data.variant || '',
        category: (data.category as AircraftCategory) || null,
        icao_designator: data.icao_designator || '',
        external_api_id: data.id,

        // Dimensions
        length: data.length || 0,
        wingspan: data.wingspan || 0,
        height: data.height || 0,
        tail_height: data.tail_height,

        // Weights
        empty_weight: data.empty_weight,
        max_takeoff_weight: data.max_takeoff_weight,
        max_ramp_weight: data.max_ramp_weight,

        // Landing gear / towing
        gear_type: (data.gear_type as GearType) || null,
        main_gear_wheels: data.main_gear_wheels,
        nose_gear_steering_angle: data.nose_gear_steering_angle,
        nose_gear_from_nose: data.nose_gear_from_nose,
        main_gear_from_nose: data.main_gear_from_nose,
        wheel_track: data.wheel_track,
        wheelbase: data.wheelbase,
        turning_radius: data.turning_radius,

        // Keep defaults for fields not in API
        model_file_path: '',
        model_scale: 1.0,
        heading_axis: 'y',
        vertical_axis: 'z',
        typical_value_new: undefined,
        typical_value_used: undefined,
        hangar_rate_typical: undefined,
        notes: '',
        is_active: true,
      })
    },
    [form]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* API Search Section - only shown for new aircraft types */}
        {showApiSearch && !isEdit && (
          <Card className="border-dashed">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Auto-fill from Database
              </CardTitle>
              <CardDescription>
                Search our aircraft database to auto-fill specifications. You can still edit any field manually.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AircraftSearchAutocomplete
                onSelect={handleApiDataSelect}
                disabled={isLoading}
                placeholder="Search by manufacturer or model (e.g., Citation CJ3, Gulfstream G650)..."
              />
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
            <TabsTrigger value="3d">3D Model</TabsTrigger>
            <TabsTrigger value="towing">Towing</TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="manufacturer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Manufacturer *</FormLabel>
                    <FormControl>
                      <Input placeholder="Cessna" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model *</FormLabel>
                    <FormControl>
                      <Input placeholder="Citation CJ3" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="citation-cj3" {...field} />
                    </FormControl>
                    <FormDescription>
                      Unique identifier (lowercase, no spaces)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="variant"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Variant</FormLabel>
                    <FormControl>
                      <Input placeholder="Plus" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AIRCRAFT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {AIRCRAFT_CATEGORY_LABELS[cat]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="icao_designator"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ICAO Designator</FormLabel>
                    <FormControl>
                      <Input placeholder="C25B" maxLength={4} {...field} />
                    </FormControl>
                    <FormDescription>4-letter ICAO code</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive aircraft types won't appear in selection lists
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes about this aircraft type..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          {/* Dimensions Tab */}
          <TabsContent value="dimensions" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground mb-4">
              All dimensions in feet, weights in pounds
            </p>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="wingspan"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wingspan (ft) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Length (ft) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Height (ft) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tail_height"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tail Height (ft)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.1"
                      value={field.value != null ? String(field.value) : ''}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                      name={field.name}
                    />
                  </FormControl>
                  <FormDescription>
                    Height of the tail (may differ from overall height)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4 pt-4">
              <FormField
                control={form.control}
                name="empty_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empty Weight (lbs)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_takeoff_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Takeoff Weight (lbs)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_ramp_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Ramp Weight (lbs)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>

          {/* 3D Model Tab */}
          <TabsContent value="3d" className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="model_file_path"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>3D Model File</FormLabel>
                  <FormControl>
                    <ModelUpload
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormDescription>
                    Upload a GLB or GLTF file for 3D visualization
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="model_scale"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model Scale</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.001"
                      value={field.value != null ? String(field.value) : '1.0'}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 1.0)}
                      onBlur={field.onBlur}
                      disabled={field.disabled}
                      name={field.name}
                    />
                  </FormControl>
                  <FormDescription>
                    Scale factor for the 3D model (1.0 = no scaling)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="heading_axis"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Heading Axis</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'y'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select axis" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="x">X</SelectItem>
                        <SelectItem value="y">Y</SelectItem>
                        <SelectItem value="z">Z</SelectItem>
                        <SelectItem value="-x">-X</SelectItem>
                        <SelectItem value="-y">-Y</SelectItem>
                        <SelectItem value="-z">-Z</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Axis the model faces (forward direction)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vertical_axis"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vertical Axis</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || 'z'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select axis" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="x">X</SelectItem>
                        <SelectItem value="y">Y</SelectItem>
                        <SelectItem value="z">Z</SelectItem>
                        <SelectItem value="-x">-X</SelectItem>
                        <SelectItem value="-y">-Y</SelectItem>
                        <SelectItem value="-z">-Z</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Axis pointing upward
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>

          {/* Towing Tab */}
          <TabsContent value="towing" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground mb-4">
              Landing gear geometry for towing calculations (all distances in feet)
            </p>

            <FormField
              control={form.control}
              name="gear_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gear Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gear type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="tricycle">{GEAR_TYPE_LABELS.tricycle}</SelectItem>
                      <SelectItem value="conventional">{GEAR_TYPE_LABELS.conventional}</SelectItem>
                      <SelectItem value="tandem">{GEAR_TYPE_LABELS.tandem}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="main_gear_wheels"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Main Gear Wheels</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="2"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Wheels per main strut</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nose_gear_steering_angle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Steering Angle (deg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="60"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Max nose gear steering angle</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nose_gear_from_nose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nose Gear from Nose (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="main_gear_from_nose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Main Gear from Nose (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="wheel_track"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wheel Track (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Main gear lateral distance</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="wheelbase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wheelbase (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Nose to main gear</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="turning_radius"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Turning Radius (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Min ground turning radius</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Form actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? 'Saving...'
              : isEdit
              ? 'Update Aircraft Type'
              : 'Create Aircraft Type'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
